import React, { useCallback, useEffect, useState } from 'react';
import { useId } from 'js/hooks/useId';
import classNames from 'classnames';
import {
  Button,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  UncontrolledDropdown,
} from 'reactstrap';
import { Icon } from 'js/components';
import { faEllipsis, faTrashAlt } from '@fortawesome/pro-regular-svg-icons';
import AsyncButton from 'js/components/Forms/AsyncButton';
import { format } from 'date-fns';
import { isNullOrWhitespace } from 'js/utils/string';
import { formatRetiredAtLabel } from 'js/utils/rubric';
import styles from './RubricEditor.module.scss';

const DATETIME_LOCAL_FMT = "yyyy-MM-dd'T'HH:mm";

/** `datetime-local` uses a local-time string, not a Date object. */
function datetimeLocalNow() {
  return format(new Date(), DATETIME_LOCAL_FMT);
}

function datetimeLocalToUtcIso(localValue) {
  const d = new Date(String(localValue ?? '').trim());
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
}

const ACTION_RETIRE = 'retire';
const ACTION_DELETE = 'delete';
const ACTION_CLEAR = 'clear';

/**
 * Single control to retire or delete a rubric question set, question, or source.
 */
export default function RubricMeasurementActions({
  retiredAt,
  onRetire,
  onClear,
  onDelete,
  retireDescription = 'Stop counting this measurement toward scores after the retirement date.',
  detailPrimary,
  detailSecondary,
  deleteNote,
  modalTitle = 'Remove measurement?',
  buttonLabel = 'Remove',
  dropdownMenuLabel = 'Remove…',
  trigger = 'button',
  ariaLabel = 'Remove measurement',
  className,
}) {
  const retireDateInputId = useId();
  const retireChoiceId = useId();
  const deleteChoiceId = useId();
  const clearChoiceId = useId();

  const [modalOpen, setModalOpen] = useState(false);
  const [action, setAction] = useState(ACTION_DELETE);
  const [dateValue, setDateValue] = useState(datetimeLocalNow);

  const retiredLabel = formatRetiredAtLabel(retiredAt);
  const isRetired = retiredLabel != null;

  useEffect(() => {
    if (!modalOpen) {
      return;
    }
    setDateValue(datetimeLocalNow());
    setAction(isRetired ? ACTION_CLEAR : ACTION_DELETE);
  }, [modalOpen, isRetired]);

  const handleOpen = useCallback(() => {
    setModalOpen(true);
  }, []);

  const handleCancel = useCallback(() => {
    setModalOpen(false);
  }, []);

  const handleConfirm = useCallback(async () => {
    if (action === ACTION_RETIRE) {
      const utc = datetimeLocalToUtcIso(dateValue);
      if (utc) {
        onRetire?.(utc);
      }
    } else if (action === ACTION_CLEAR) {
      onClear?.();
    } else if (action === ACTION_DELETE) {
      await onDelete?.();
    }
    setModalOpen(false);
  }, [action, dateValue, onRetire, onClear, onDelete]);

  const confirmLabel =
    action === ACTION_RETIRE
      ? 'Retire'
      : action === ACTION_CLEAR
      ? 'Clear retirement'
      : 'Delete';

  const confirmColor =
    action === ACTION_DELETE
      ? 'danger'
      : action === ACTION_RETIRE
      ? 'warning'
      : 'primary';

  const showRetiredBadge = trigger === 'button' && isRetired;

  return (
    <>
      {trigger === 'dropdown' ? (
        <UncontrolledDropdown
          className={classNames(
            styles['measurement-action-menu'],
            styles['source-action-menu'],
            className
          )}
        >
          <DropdownToggle
            color="link"
            className={styles['measurement-action-trigger']}
            aria-label={ariaLabel}
          >
            <Icon icon={faEllipsis} />
          </DropdownToggle>
          <DropdownMenu right>
            <DropdownItem onClick={handleOpen}>
              {dropdownMenuLabel}
            </DropdownItem>
          </DropdownMenu>
        </UncontrolledDropdown>
      ) : (
        <div className={classNames(styles['measurement-actions'], className)}>
          {showRetiredBadge && (
            <span
              className={styles['retired-badge']}
              title="Retired measurement"
            >
              Retired {retiredLabel}
            </span>
          )}
          <Button
            type="button"
            color="primary"
            className="btn-action-alt"
            onClick={handleOpen}
            aria-label={ariaLabel}
          >
            <Icon icon={faTrashAlt} className="icon icon-left" />
            {buttonLabel}
          </Button>
        </div>
      )}

      <Modal isOpen={modalOpen} toggle={handleCancel}>
        <ModalHeader toggle={handleCancel}>{modalTitle}</ModalHeader>
        <ModalBody>
          <MeasurementModalDetail
            primary={detailPrimary}
            secondary={detailSecondary}
          />

          <div className={styles['measurement-action-choices']}>
            {isRetired && (
              <div className={styles['measurement-action-choice']}>
                <Label check className={styles['measurement-action-radio']}>
                  <Input
                    type="radio"
                    name={`measurement-action-${clearChoiceId}`}
                    checked={action === ACTION_CLEAR}
                    onChange={() => setAction(ACTION_CLEAR)}
                  />
                  <span className={styles['measurement-action-choice-title']}>
                    Clear retirement
                  </span>
                </Label>
                <p className={styles['measurement-action-choice-desc']}>
                  Resume using this measurement in rubric scores (subject to
                  assessment data).
                </p>
              </div>
            )}

            <div className={styles['measurement-action-choice']}>
              <Label check className={styles['measurement-action-radio']}>
                <Input
                  type="radio"
                  name={`measurement-action-${deleteChoiceId}`}
                  checked={action === ACTION_DELETE}
                  onChange={() => setAction(ACTION_DELETE)}
                />
                <span className={styles['measurement-action-choice-title']}>
                  Delete
                </span>
              </Label>
              <p className={styles['measurement-action-choice-desc']}>
                Remove from the rubric definition entirely. This cannot be
                undone from history; re-add the measurement if needed.
                {deleteNote ? <> {deleteNote}</> : null}
              </p>
            </div>

            {!isRetired && (
              <div className={styles['measurement-action-choice']}>
                <Label check className={styles['measurement-action-radio']}>
                  <Input
                    type="radio"
                    name={`measurement-action-${retireChoiceId}`}
                    checked={action === ACTION_RETIRE}
                    onChange={() => setAction(ACTION_RETIRE)}
                  />
                  <span className={styles['measurement-action-choice-title']}>
                    Retire
                  </span>
                </Label>
                <p className={styles['measurement-action-choice-desc']}>
                  {retireDescription}
                </p>
                {action === ACTION_RETIRE && (
                  <div className={styles['measurement-action-choice-detail']}>
                    <FormGroupRetireDateTime
                      id={retireDateInputId}
                      value={dateValue}
                      onChange={setDateValue}
                    />
                  </div>
                )}
              </div>
            )}
          </div>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={handleCancel}>
            Cancel
          </Button>
          <AsyncButton color={confirmColor} onClick={handleConfirm}>
            {confirmLabel}
          </AsyncButton>
        </ModalFooter>
      </Modal>
    </>
  );
}

function MeasurementModalDetail({ primary, secondary }) {
  const primaryLabel = String(primary ?? '').trim() || '—';
  const secondaryLabel = !isNullOrWhitespace(secondary)
    ? String(secondary).trim()
    : '—';

  return (
    <div className={styles['measurement-modal-detail']}>
      <div className={styles['measurement-modal-detail-primary']}>
        {primaryLabel}
      </div>
      <div
        className={classNames(
          styles['measurement-modal-detail-secondary'],
          'line-clamp-2'
        )}
      >
        {secondaryLabel}
      </div>
    </div>
  );
}

function FormGroupRetireDateTime({ id, value, onChange }) {
  return (
    <div className="form-group mb-0">
      <Label for={id}>Retire after</Label>
      <Input
        id={id}
        type="datetime-local"
        value={value}
        step={60}
        onChange={(e) => onChange(e.target.value)}
      />
    </div>
  );
}
